import { useContext, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";

import type {
  ApiError,
  FileUploadSuccessResponse,
} from "@sperrdatei/dto/types";

type UploadPageProps = object;

export const UploadPage: React.FC<UploadPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<FileUploadSuccessResponse | undefined>(
    undefined,
  );

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (file && file.name.toLowerCase().endsWith(".csv")) {
      setSelectedFile(file);
      setError(undefined);
    } else {
      setSelectedFile(undefined);
      setError("Bitte wählen Sie eine CSV-Datei aus.");
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files?.[0];
    if (file && file.name.toLowerCase().endsWith(".csv")) {
      setSelectedFile(file);
      setError(undefined);
    } else {
      setSelectedFile(undefined);
      setError("Bitte wählen Sie eine CSV-Datei aus.");
    }
  };

  const uploadFile = async (): Promise<void> => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await apiFetch("/api/number/upload", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setResult(await response.json());
        return;
      }

      if (response.status === 400) {
        const error = (await response.json()) as ApiError;
        if (error.code === "MAXIUM_NUMBERS_EXCEEDED") {
          setError("Die Datei enthält zu viele Nummern");
          return;
        }
      }

      if (response.status === 413) {
        setError(
          "Die Datei überschreitet die maximal zulässige Größe von 10MB",
        );
        return;
      }

      navigate("/error");
    }
  };

  return (
    <div className="container ms-0">
      <h3 className="page-caption mb-3">Datei upload</h3>
      {result ? (
        <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
          <h6>
            {result.errors.length > 0
              ? "Vorgang mit Fehler abgeschlossen"
              : "Vorgang abgeschlossen"}
          </h6>
          <p>
            <strong>Ergebnis</strong>: <br />
            <span>
              Insgesamt {result.inserted > 1 ? "wurden " : "wurde "}
              <strong>
                {result.inserted === 0 ? "keine" : result.inserted}
              </strong>{" "}
              neue
              {result.inserted > 1 ? " Nummern" : " Nummer"} gesperrt.
              <br />
              <strong>
                {result.updated === 0 ? "Keine" : result.updated}
              </strong>{" "}
              {result.updated > 1 ? "Nummern waren" : "Nummer war"} bereits
              eingetragen und wurden aktualisiert.
              <br />
              <br />
              {result.errors.length > 0 ? (
                <>
                  <strong>Fehler:</strong>
                  <br />
                  <ol className="list-group">
                    {result.errors.map((error, idx) => (
                      <li
                        key={idx}
                        className="d-flex justify-content-between align-items-start"
                      >
                        <div className="ms-2 me-auto fw-light">{error}</div>
                      </li>
                    ))}
                  </ol>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => window.location.reload()}
                  >
                    OK
                  </button>
                </>
              ) : null}
            </span>
          </p>
        </div>
      ) : (
        <>
          <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
            <p>
              Hier können Sie eine Textdatei (*.csv) mit zu sperrenden Nummern
              hochladen.
            </p>
            <div className="card card-body border-0 px-0 py-0">
              <p>
                <i className="bi bi-question-square color-primary"></i>{" "}
                <a
                  href="#"
                  className="link-dark"
                  onClick={() => setShowHelp(!showHelp)}
                >
                  Hilfe
                </a>
              </p>
              <div className={showHelp ? "collapse show" : "collapse"}>
                <strong>Satzbeschreibung:</strong>
                <pre>Rufnummer;Art der Rufnummer;Art der Sperrung</pre>
                <div className="table-responsive">
                  <table className="table align-top">
                    <thead>
                      <tr>
                        <th>Rufnummer</th>
                        <th>Art der Rufnummer</th>
                        <th>Art der Sperrung</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          Vorwahl mit führender Null und nationale
                          Teilnehmernummer ohne Trennzeichen
                        </td>
                        <td>
                          Kennzeichnet die Nummer als privat oder gewerblich
                        </td>
                        <td>
                          Definiert die Art der Sperrung für gewerbliche Nummern
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Beispiele:</strong>
                          <br /> 04012345, <br />
                          017845627
                        </td>
                        <td>
                          <strong>0</strong> = Gewerblich
                          <br />
                          <strong>1</strong> = Privat
                        </td>
                        <td className="align-top">
                          <strong>1</strong> = Gesperrt für alle Befragungen
                          <br />
                          <strong>2</strong> = Gesperrt nur für
                          Bevölkerungsbefragungen
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-4">
                    <strong>Hinweise:</strong>
                  </div>
                  <ul>
                    <li>Rufnummerblöcke sind in der Datei nicht zulässig</li>
                    <li>
                      Pro Datei können maximal 10.000 Nummern gesperrt werden
                    </li>
                    <li>Die maximale Dateigröße ist 10MB</li>
                    <li>
                      Nummern in ungültigem Format (z.B. ungültige Vorwahl,
                      Trennzeichen zwischen Vorwahl und Rufnummer, fehlendes
                      oder falsches Privat-Gewerblich Kennzeichen) werden nicht
                      eingetragen.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8 col-md-10">
              <div className="card">
                <div className="card-body">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      uploadFile();
                    }}
                  >
                    {!selectedFile && (
                      <div className="mb-3">
                        {error ? (
                          <p className="small text-danger d-flex align-items-center gap-1">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            {error}
                          </p>
                        ) : (
                          <label htmlFor="csvFile" className="form-label">
                            CSV-Datei auswählen
                          </label>
                        )}

                        <div
                          className={`border rounded p-5 text-center ${
                            isDragOver
                              ? "border-primary bg-light"
                              : "border-secondary"
                          }`}
                          onDrop={handleDrop}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOver(true);
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            setIsDragOver(false);
                          }}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            document.getElementById("csvFile")?.click()
                          }
                        >
                          <input
                            type="file"
                            id="csvFile"
                            className="d-none"
                            accept=".csv"
                            onChange={handleFileChange}
                          />

                          <div className="mb-3">
                            <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                          </div>

                          <small className="text-muted">
                            Nur CSV-Dateien werden akzeptiert
                          </small>
                        </div>
                      </div>
                    )}

                    {selectedFile && (
                      <div className="mb-3">
                        {error ? (
                          <p className="small text-danger d-flex align-items-center gap-1">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                            {error}
                          </p>
                        ) : (
                          <label htmlFor="csvFile" className="form-label">
                            Ausgewählte Datei
                          </label>
                        )}
                        <div className="alert alert-secondary d-flex">
                          <div className="me-auto">
                            <i className="bi bi-file-earmark-text-fill"></i>{" "}
                            <strong>{selectedFile.name}</strong>{" "}
                            <span>
                              <small className="text-muted">
                                ({(selectedFile.size / 1024).toFixed(2)} KB)
                              </small>
                            </span>
                          </div>
                          <div>
                            <i
                              title="Eintrag löschen"
                              className="bi bi-x-circle-fill ms-auto focus-alert"
                              onClick={() => {
                                setSelectedFile(undefined);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {!selectedFile && (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!selectedFile}
                        onClick={(e) => {
                          e.preventDefault();
                          uploadFile();
                        }}
                      >
                        <i className="bi bi-upload me-2"></i>
                        Datei hochladen
                      </button>
                    )}

                    {selectedFile && (
                      <button
                        type="submit"
                        className="btn btn-danger"
                        disabled={!selectedFile}
                      >
                        Nummern Sperren
                      </button>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
